import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, ArrowLeft, CheckCircle2, ArrowRight } from 'lucide-react';
import { MasterClassPlayer } from './MasterClassPlayer';
import { MasterClassPlaylist } from './MasterClassPlaylist';
import { SerieDescription } from './SerieDescription';
import type { Serie, Aula } from '@/types/masterclass';
import { useMasterClassProgress } from '@/hooks/useMasterClassProgress';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import lessonsData from '@/data/masterclass/lessons.json';
import { useMasterClassKeyboard } from '@/hooks/useMasterClassKeyboard';

interface MasterClassFullScreenProps {
  serie: Serie;
  onClose: () => void;
}

export function MasterClassFullScreen({ serie, onClose }: MasterClassFullScreenProps) {
  useLanguage();
  const [currentAulaId, setCurrentAulaId] = useState<string | null>(null);
  const { aulas: progressAulas, updateProgress } = useMasterClassProgress(serie.id);
  
  const aulas = lessonsData.filter(l => l.serieId === serie.id) as Aula[];
  const currentAula = aulas.find(a => a.id === currentAulaId);
  const currentIndex = aulas.findIndex(a => a.id === currentAulaId);
  const hasNextLesson = currentIndex < aulas.length - 1;

  // Auto-start primeira aula
  useEffect(() => {
    if (!currentAulaId && aulas.length > 0) {
      const proximaNaoConcluida = aulas.find(a => {
        const progress = progressAulas[a.id];
        return !progress || progress.status !== 'concluida';
      });
      setCurrentAulaId(proximaNaoConcluida?.id || aulas[0].id);
    }
  }, [aulas, currentAulaId, progressAulas]);

  const handleMarkComplete = () => {
    if (currentAulaId) {
      updateProgress(currentAulaId, 0, 'concluida', serie.aulas.length);
      
      // Toast de sucesso
      toast({
        title: translateInterfaceText("Aula concluída! 🎉"),
        description: translateInterfaceText("Seu progresso foi salvo."),
      });
      
      // Fechar modal
      setTimeout(() => {
        onClose();
      }, 500);
    }
  };

  const handleNextLesson = () => {
    if (hasNextLesson) {
      setCurrentAulaId(aulas[currentIndex + 1].id);
    }
  };

  const handleTimeUpdate = (time: number) => {
    if (currentAulaId) {
      updateProgress(currentAulaId, time, 'em_andamento', serie.aulas.length);
    }
  };

  const handleVideoEnded = () => {
    handleMarkComplete();
  };

  // Atalhos de teclado
  useMasterClassKeyboard({
    onClose: onClose,
  });

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      
      {/* HEADER AZUL - FIXO NO TOPO */}
      <div className="mc-modal__header flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <img 
            src="/host-vsl-logo.png" 
            alt={translateInterfaceText("Host VSL")}
            className="h-8 w-auto"
          />
          <h2 className="text-lg font-semibold text-white">
            {serie.titulo}
          </h2>
        </div>
        
        <button 
          onClick={onClose}
          className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
          aria-label={translateInterfaceText("Fechar")}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* CONTEÚDO PRINCIPAL */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* ESQUERDA: Player + Descrição */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-5xl mx-auto p-8">
            
            {/* Player */}
            <div className="mc-player w-full">
              <MasterClassPlayer 
                aula={currentAula || null}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnded}
              />
            </div>
            
            {/* Descrição */}
            <div className="mt-8 bg-white rounded-xl p-6 shadow-sm mc-video-description">
              <SerieDescription aula={currentAula || null} />
            </div>
            
          </div>
        </div>

        {/* DIREITA: Playlist cinza - fixada */}
        <div className="w-[420px] border-l border-gray-200 flex flex-col bg-gray-50">
          <div className="p-6 border-b border-gray-200 shrink-0">
            <h3 className="text-sm font-semibold text-gray-900">
              {translateInterfaceText("Aulas (")}{aulas.length})
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              {serie.categoria[0] || translateInterfaceText("Master Class")}
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            <MasterClassPlaylist 
              aulas={aulas}
              currentAulaId={currentAulaId}
              serieId={serie.id}
              onSelect={setCurrentAulaId}
            />
          </div>
        </div>
      </div>

      {/* FOOTER COM BOTÕES - FIXO NA BASE */}
      <div className="flex items-center justify-between p-4 border-t shrink-0 bg-white" style={{ borderColor: 'var(--gray-200)' }}>
        <Button 
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {translateInterfaceText("Voltar")}</Button>
        
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleMarkComplete}
            size="sm"
            className="btn-primary-mc"
            aria-label={translateInterfaceText("Marcar aula como concluída")}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            {translateInterfaceText("Marcar como Concluído")}</Button>
          
          {hasNextLesson && (
            <Button 
              onClick={handleNextLesson}
              variant="outline"
              size="sm"
              className="btn-outline-mc"
            >
              {translateInterfaceText("Próxima Aula")}<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

    </div>
  );
}
